import React, { useState } from "react";
import {
    GraduationCap,
    ShieldCheck,
    Award,
    Clock,
    Users,
    Construction,
    Forklift,
    Truck,
    HardHat,
    Wrench,
    ArrowUpRight,
    CheckCircle2,
    Send,
    Loader2,
    Sparkles,
    Building2,
    Phone,
    Mail,
    MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { nextCourseDateLabel } from "../lib/nextCourseDate";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Corsi attivabili NSP (tabella ufficiale, D.Lgs. 81/08 e s.m.i.). */
const COURSES = [
    {
        id: "escavatori",
        title: "Escavatori, Caricatori e Terne",
        subtitle:
            "Conduzione escavatori idraulici, caricatori frontali e terne — teorico/pratico",
        icon: Construction,
        duration: "16 ore",
        validity: "Attestato abilitante + patentino plastificato",
        groups: "Gruppi da 6 o da 12 persone",
        law: "Artt. 37 e 73 c.5 D.Lgs. 81/08 · Accordo Stato-Regioni 17.04.2025",
        price: "120,00 + IVA",
        color: "from-nsp-green to-nsp-green-bright",
    },
    {
        id: "gru-scarrabili",
        title: "Gru Fissa per Scarrabili",
        subtitle: "Operatore addetto all'utilizzo — teorico/pratico",
        icon: Truck,
        duration: "8 ore",
        validity: "Attestato abilitante + patentino plastificato",
        groups: "Gruppi da 6 o da 12 persone",
        law: "Artt. 37 e 73 c.4 D.Lgs. 81/08 · correttivo D.Lgs. 106/09",
        price: "90,00 + IVA",
        color: "from-nsp-green to-nsp-green-bright",
    },
    {
        id: "ponteggi",
        title: "Ponteggi",
        subtitle:
            "Montaggio, smontaggio e trasformazione — lavoratori e preposti",
        icon: Building2,
        duration: "28 ore",
        validity: "Attestato (senza patentino)",
        groups: "Gruppi da 6 o da 12 persone",
        law: "Art. 136 c.8 e Allegato XXI D.Lgs. 81/08",
        price: "280,00 + IVA",
        color: "from-nsp-green to-nsp-green-bright",
    },
];

const initialForm = {
    name: "",
    email: "",
    phone: "",
    company: "",
    course_id: "",
    participants: 1,
    notes: "",
    consent_privacy: false,
    website: "", // honeypot
};

export const TrainingCourses = () => {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const set = (field) => (e) => {
        const value =
            e.target.type === "checkbox"
                ? e.target.checked
                : e.target.type === "number"
                  ? Math.max(1, parseInt(e.target.value || "1", 10))
                  : e.target.value;
        setForm((f) => ({ ...f, [field]: value }));
    };

    const selectCourse = (id) => {
        setForm((f) => ({ ...f, course_id: id }));
        // Scroll verso il form
        setTimeout(() => {
            document
                .getElementById("training-form")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
    };

    const canSubmit =
        form.name.trim().length >= 2 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
        form.phone.trim().length >= 5 &&
        form.course_id &&
        form.consent_privacy;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit || submitting) return;
        setSubmitting(true);
        try {
            const res = await fetch(`${API_BASE}/training/request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: form.name.trim(),
                    email: form.email.trim(),
                    phone: form.phone.trim(),
                    company: form.company.trim() || null,
                    course_id: form.course_id,
                    participants: form.participants,
                    notes: form.notes.trim() || null,
                    consent_privacy: form.consent_privacy,
                    website: form.website,
                }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "Errore invio richiesta");
            }
            toast.success("Richiesta inviata! Ti contatteremo entro 24 ore.");
            setSuccess(true);
            setForm(initialForm);
        } catch (err) {
            toast.error(err.message || "Impossibile inviare la richiesta. Riprova.");
        } finally {
            setSubmitting(false);
        }
    };

    const selectedCourse = COURSES.find((c) => c.id === form.course_id);

    return (
        <section
            id="corsi"
            data-testid="training-section"
            className="bg-white relative overflow-hidden"
        >
            {/* Background pattern */}
            <div
                aria-hidden="true"
                className="absolute inset-0 opacity-[0.04] pointer-events-none"
                style={{
                    backgroundImage:
                        "repeating-linear-gradient(45deg, #0a3b25 0 1px, transparent 1px 60px)",
                }}
            />

            <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-14 py-14 lg:py-20">
                {/* HEADER */}
                <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-2 bg-white border border-nsp-green-100 text-nsp-green px-3 py-1.5 mb-5 text-[11px] font-semibold uppercase tracking-[0.2em] rounded-full shadow-sm">
                        <GraduationCap className="w-3 h-3 text-nsp-yellow-dark" /> Formazione & Patentini
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase text-nsp-green leading-[0.95] tracking-tight">
                        Patentini <span className="text-nsp-yellow-dark">per cantieri.</span>
                    </h2>
                    <p className="mt-5 text-gray-600 text-base leading-relaxed max-w-2xl">
                        Corsi di formazione professionali per ottenere il <b>patentino di
                        abilitazione</b> all'uso delle attrezzature da lavoro come previsto
                        dall'<b>Accordo Stato-Regioni del 17/04/2025</b> e dal D.Lgs. 81/08.
                        Attestato finale riconosciuto sul territorio nazionale.
                    </p>
                </div>

                {/* GRID CORSI */}
                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {COURSES.map((c) => (
                        <CourseCard
                            key={c.id}
                            course={c}
                            onSelect={() => selectCourse(c.id)}
                            isSelected={form.course_id === c.id}
                        />
                    ))}

                    {/* Card "Altro corso" */}
                    <button
                        type="button"
                        data-testid="course-card-other"
                        onClick={() => selectCourse("other")}
                        className={`text-left bg-nsp-bg border-2 border-dashed transition-all p-6 flex flex-col items-start gap-3 group ${
                            form.course_id === "other"
                                ? "border-nsp-green bg-nsp-green/5"
                                : "border-nsp-border hover:border-nsp-green hover:bg-white"
                        }`}
                    >
                        <div className="w-12 h-12 bg-white border border-nsp-border group-hover:border-nsp-green flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-nsp-green" strokeWidth={2.5} />
                        </div>
                        <div>
                            <div className="font-display text-xl uppercase text-nsp-green tracking-tight">
                                Altro corso
                            </div>
                            <div className="text-zinc-600 text-sm mt-1">
                                Non trovi quello che ti serve? Compila il modulo e indica nelle
                                note il corso d'interesse.
                            </div>
                        </div>
                        <div className="text-xs font-bold uppercase tracking-widest text-nsp-green mt-auto inline-flex items-center gap-1.5">
                            Richiedi info <ArrowUpRight className="w-3 h-3" />
                        </div>
                    </button>
                </div>

                {/* FORM */}
                <div
                    id="training-form"
                    data-testid="training-form-section"
                    className="mt-16 scroll-mt-28"
                >
                    <div
                        data-testid="training-next-course-banner"
                        className="mb-8 bg-nsp-yellow border-l-4 border-nsp-green rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 shadow-sm"
                    >
                        <div className="flex items-center gap-2 text-nsp-green font-bold uppercase tracking-widest text-[11px] flex-shrink-0">
                            <Clock className="w-4 h-4" />
                            Prossimo corso in partenza
                        </div>
                        <div className="font-display uppercase text-xl text-nsp-green tracking-tight">
                            {nextCourseDateLabel()}
                        </div>
                        <div className="text-nsp-green/70 text-xs sm:ml-auto">
                            Posti limitati — invia subito la richiesta per riservare il tuo.
                        </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    {/* COLONNA SINISTRA: vantaggi */}
                    <div className="lg:col-span-5">
                        <div className="bg-nsp-green text-white p-8 lg:p-10 rounded-3xl relative overflow-hidden">
                            <div
                                aria-hidden="true"
                                className="absolute -top-6 -right-6 opacity-10"
                            >
                                <GraduationCap className="w-48 h-48" strokeWidth={1.2} />
                            </div>
                            <div className="relative">
                                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-nsp-yellow">
                                    Perché scegliere NSP
                                </div>
                                <h3 className="font-display text-3xl uppercase tracking-tight mt-2">
                                    Formazione
                                    <br />
                                    a 360°
                                </h3>
                                <ul className="mt-8 space-y-4">
                                    <Benefit text="Corsi a norma D.Lgs. 81/08 e Accordo Stato-Regioni" />
                                    <Benefit text="Docenti qualificati con esperienza decennale in cantiere" />
                                    <Benefit text="Sessioni teoriche + pratiche sui mezzi reali NSP" />
                                    <Benefit text="Attestato e abilitazione riconosciuti sul territorio nazionale" />
                                    <Benefit text="Aggiornamenti periodici e supporto al rinnovo del patentino" />
                                    <Benefit text="Corsi anche presso la tua sede aziendale (su richiesta)" />
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* COLONNA DESTRA: form */}
                    <div className="lg:col-span-7">
                        <div className="bg-white border-2 border-nsp-border p-6 sm:p-8 lg:p-10">
                            <div className="flex items-start gap-3 mb-6">
                                <span className="font-display text-nsp-green text-3xl">→</span>
                                <div>
                                    <h3 className="font-display text-2xl uppercase text-nsp-green tracking-tighter">
                                        Richiedi informazioni
                                    </h3>
                                    <p className="text-zinc-500 text-sm mt-1">
                                        Compila il modulo. Ti contatteremo entro 24 ore con i
                                        dettagli del corso (date, sede, costi).
                                    </p>
                                </div>
                            </div>

                            {success ? (
                                <SuccessBox onReset={() => setSuccess(false)} />
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {/* Honeypot anti-bot */}
                                    <input
                                        type="text"
                                        name="website"
                                        value={form.website}
                                        onChange={set("website")}
                                        autoComplete="off"
                                        tabIndex={-1}
                                        aria-hidden="true"
                                        className="hidden"
                                    />

                                    {/* Corso selezionato (badge) */}
                                    {selectedCourse && (
                                        <div className="bg-nsp-green text-nsp-yellow px-4 py-3 flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                                                <div className="min-w-0">
                                                    <div className="text-[10px] uppercase tracking-widest opacity-80">
                                                        Corso selezionato
                                                    </div>
                                                    <div className="font-bold uppercase tracking-wide truncate">
                                                        {selectedCourse.title}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {form.course_id === "other" && (
                                        <div className="bg-nsp-yellow text-nsp-green px-4 py-3">
                                            <div className="text-[10px] uppercase tracking-widest opacity-80">
                                                Corso selezionato
                                            </div>
                                            <div className="font-bold uppercase tracking-wide">
                                                Altro (specifica nelle note)
                                            </div>
                                        </div>
                                    )}

                                    {/* Selezione corso se non già selezionato */}
                                    {!form.course_id && (
                                        <Field label="Corso d'interesse *">
                                            <select
                                                data-testid="training-course-select"
                                                value={form.course_id}
                                                onChange={set("course_id")}
                                                required
                                                className="h-12 w-full bg-white border border-nsp-border px-3 focus:outline-none focus:border-nsp-green"
                                            >
                                                <option value="">Seleziona il corso...</option>
                                                {COURSES.map((c) => (
                                                    <option key={c.id} value={c.id}>
                                                        {c.title}
                                                    </option>
                                                ))}
                                                <option value="other">Altro / non in elenco</option>
                                            </select>
                                        </Field>
                                    )}

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <Field label="Nome e Cognome *">
                                            <FieldInput
                                                testid="training-name"
                                                icon={Users}
                                                type="text"
                                                value={form.name}
                                                onChange={set("name")}
                                                maxLength={120}
                                                autoComplete="name"
                                                required
                                            />
                                        </Field>
                                        <Field label="Azienda (opzionale)">
                                            <FieldInput
                                                testid="training-company"
                                                icon={Building2}
                                                type="text"
                                                value={form.company}
                                                onChange={set("company")}
                                                maxLength={200}
                                                autoComplete="organization"
                                            />
                                        </Field>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <Field label="Email *">
                                            <FieldInput
                                                testid="training-email"
                                                icon={Mail}
                                                type="email"
                                                value={form.email}
                                                onChange={set("email")}
                                                maxLength={150}
                                                autoComplete="email"
                                                required
                                            />
                                        </Field>
                                        <Field label="Telefono *">
                                            <FieldInput
                                                testid="training-phone"
                                                icon={Phone}
                                                type="tel"
                                                value={form.phone}
                                                onChange={set("phone")}
                                                maxLength={30}
                                                autoComplete="tel"
                                                inputMode="tel"
                                                required
                                            />
                                        </Field>
                                    </div>

                                    <Field label="Numero partecipanti">
                                        <input
                                            data-testid="training-participants"
                                            type="number"
                                            min={1}
                                            max={50}
                                            value={form.participants}
                                            onChange={set("participants")}
                                            className="h-12 w-32 bg-white border border-nsp-border px-3 focus:outline-none focus:border-nsp-green"
                                        />
                                    </Field>

                                    <Field label="Note (opzionale)" full>
                                        <div className="relative">
                                            <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-zinc-400" />
                                            <textarea
                                                data-testid="training-notes"
                                                rows={3}
                                                value={form.notes}
                                                onChange={set("notes")}
                                                maxLength={1000}
                                                placeholder={
                                                    form.course_id === "other"
                                                        ? "Indica qui il corso d'interesse e altre richieste..."
                                                        : "Disponibilità date, esigenze particolari, ecc."
                                                }
                                                className="w-full bg-white border border-nsp-border pl-10 pr-3 py-3 focus:outline-none focus:border-nsp-green"
                                            />
                                        </div>
                                    </Field>

                                    {/* Consenso privacy */}
                                    <label className="flex items-start gap-3 cursor-pointer p-3 bg-nsp-bg border border-nsp-border hover:border-nsp-green">
                                        <input
                                            type="checkbox"
                                            data-testid="training-consent"
                                            checked={form.consent_privacy}
                                            onChange={set("consent_privacy")}
                                            className="accent-nsp-green w-4 h-4 mt-0.5 flex-shrink-0"
                                        />
                                        <span className="text-xs text-zinc-700 leading-relaxed">
                                            Ho letto e accetto la{" "}
                                            <a href="/privacy" target="_blank" rel="noreferrer" className="text-nsp-green underline font-bold">privacy policy</a>{" "}
                                            e acconsento
                                            al trattamento dei miei dati personali per essere
                                            ricontattato in merito al corso richiesto. *
                                        </span>
                                    </label>

                                    {/* Submit */}
                                    <button
                                        type="submit"
                                        data-testid="training-submit"
                                        disabled={!canSubmit || submitting}
                                        className="w-full h-14 rounded-full bg-nsp-green text-nsp-yellow font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-3 hover:bg-nsp-green-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-md active:scale-95"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" /> Invio in corso...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="w-4 h-4" /> Invia Richiesta
                                            </>
                                        )}
                                    </button>

                                    <p className="text-[11px] text-zinc-500 text-center">
                                        Risposta garantita entro 24 ore lavorative dal nostro team.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ====== SOTTOCOMPONENTI ====== */

const CourseCard = ({ course, onSelect, isSelected }) => {
    const Icon = course.icon;
    return (
        <button
            type="button"
            data-testid={`course-card-${course.id}`}
            onClick={onSelect}
            className={`text-left rounded-3xl border transition-all p-6 flex flex-col gap-4 group relative overflow-hidden bg-white ${
                isSelected
                    ? "border-nsp-green shadow-2xl shadow-nsp-green/20 -translate-y-1"
                    : "border-gray-100 hover:border-nsp-green-100 hover:shadow-xl hover:shadow-nsp-green/10 hover:-translate-y-1"
            }`}
        >
            {isSelected && (
                <div className="absolute top-4 right-4 bg-nsp-green text-nsp-yellow text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full">
                    Selezionato
                </div>
            )}

            {/* Icona */}
            <div className="w-14 h-14 bg-gradient-to-br from-nsp-green to-nsp-green-800 rounded-2xl flex items-center justify-center shadow-md shadow-nsp-green/20">
                <Icon className="w-6 h-6 text-nsp-yellow" strokeWidth={2.2} />
            </div>

            {/* Titolo */}
            <div>
                <h3 className="font-display text-xl uppercase text-nsp-green tracking-tight leading-tight">
                    {course.title}
                </h3>
                <p className="text-gray-500 text-sm mt-1">{course.subtitle}</p>
            </div>

            {/* Dettagli */}
            <div className="space-y-1.5 text-xs text-gray-600 mt-auto">
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-nsp-green-50 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-3 h-3 text-nsp-green" />
                    </div>
                    <span>{course.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-nsp-green-50 flex items-center justify-center flex-shrink-0">
                        <Award className="w-3 h-3 text-nsp-green" />
                    </div>
                    <span>{course.validity}</span>
                </div>
                {course.groups && (
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-nsp-green-50 flex items-center justify-center flex-shrink-0">
                            <Users className="w-3 h-3 text-nsp-green" />
                        </div>
                        <span>{course.groups}</span>
                    </div>
                )}
                {course.law && (
                    <div className="text-[10px] text-gray-400 leading-snug pt-1">
                        {course.law}
                    </div>
                )}
            </div>

            {/* Footer prezzo + CTA */}
            <div className="pt-4 border-t border-gray-100 flex items-center justify-between gap-2">
                <div>
                    <div className="text-[9px] uppercase tracking-widest text-gray-400">
                        Prezzo a persona
                    </div>
                    <div className="font-display text-lg text-nsp-green leading-none mt-0.5">
                        {course.price === "XXXXXX" ? "Su richiesta" : `€ ${course.price}`}
                    </div>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-nsp-green inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Richiedi <ArrowUpRight className="w-3 h-3 group-hover:rotate-45 transition-transform" />
                </span>
            </div>
        </button>
    );
};

const Benefit = ({ text }) => (
    <li className="flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-nsp-yellow flex-shrink-0 mt-0.5" strokeWidth={2.5} />
        <span className="text-sm leading-relaxed text-white/90">{text}</span>
    </li>
);

const Field = ({ label, children, full }) => (
    <div className={full ? "col-span-full" : ""}>
        <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-600 mb-1.5">
            {label}
        </label>
        {children}
    </div>
);

const FieldInput = ({ testid, icon: Icon, ...props }) => (
    <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />}
        <input
            data-testid={testid}
            {...props}
            className={`h-12 w-full bg-white border border-nsp-border ${Icon ? "pl-10" : "pl-3"} pr-3 focus:outline-none focus:border-nsp-green`}
        />
    </div>
);

const SuccessBox = ({ onReset }) => (
    <div
        data-testid="training-success"
        className="bg-nsp-green text-white p-6 rounded-2xl text-center"
    >
        <CheckCircle2 className="w-12 h-12 text-nsp-yellow mx-auto" />
        <h4 className="font-display text-2xl uppercase tracking-tight mt-3">
            Richiesta inviata!
        </h4>
        <p className="text-white/80 text-sm mt-2">
            Grazie. Un nostro consulente ti contatterà entro 24 ore lavorative con tutti i
            dettagli del corso richiesto.
        </p>
        <button
            type="button"
            onClick={onReset}
            data-testid="training-reset"
            className="mt-5 inline-flex items-center gap-2 bg-nsp-yellow text-nsp-green px-5 py-2.5 font-bold uppercase tracking-wider text-xs hover:bg-white transition-colors"
        >
            Invia un'altra richiesta
        </button>
    </div>
);

export default TrainingCourses;
